import { ForwardedRef, forwardRef, useMemo } from "react";
import { MultiValue, OnChangeValue, SelectInstance } from "react-select";
import Creatable from "react-select/creatable";
import { StateManagerProps } from "react-select/dist/declarations/src/useStateManager";
import { useLazyQuery } from "@apollo/client";
import useSelectStyles from "utils/useSelectStyles";
import type {
  IdValue,
  IdValueSelectProps,
} from "components/Shared/Select/useIdValue";
import { gql } from "__generated__/gql";
import useFetchOptions from "components/Shared/Select/useFetchOptions";
import { RetailerTagsSelectOptionsQuery } from "__generated__/gql/graphql";
import { useActiveBrandIdContext } from "utils/apollo/hooks/brand";
import { ADMIN_CONTEXT } from "utils/apollo/hooks/admin";

type Option = { value: string };

const optionsQuery = gql(/* GraphQL */ `
  query RetailerTagsSelectOptions {
    retailerTagsList
  }
`);

function getOptions(data: RetailerTagsSelectOptionsQuery) {
  return data.retailerTagsList?.map((value) => ({ value } as Option));
}

function getOptionLabel(option: Option) {
  return option.value;
}

type Props<IsMulti extends boolean = false> = Omit<
  StateManagerProps<Option, IsMulti>,
  "onChange" | "value"
> &
  IdValueSelectProps<IsMulti> & {
    isAdmin?: boolean;
  };

function RetailerTagsSelect<IsMulti extends boolean = false>(
  {
    isAdmin,
    isMulti,
    onChange,
    value: rawValue,
    isLoading,
    ...rest
  }: Props<IsMulti>,
  ref: ForwardedRef<SelectInstance<Option, IsMulti>>,
) {
  const styleProps = useSelectStyles<Option, IsMulti>();

  const brandContext = useActiveBrandIdContext();

  const lazyQuery = useLazyQuery(optionsQuery, {
    context: isAdmin ? ADMIN_CONTEXT : brandContext,
    fetchPolicy: "cache-and-network",
  });

  const fetchOptionsProps = useFetchOptions({
    getOptions,
    lazyQuery,
    menuIsOpen: rest.menuIsOpen,
  });

  const value = useMemo(() => {
    const valueArray: string[] = Array.isArray(rawValue)
      ? rawValue
      : (rawValue && [rawValue]) || [];
    const valueOptions = valueArray.map((v) => ({ value: v }));
    return isMulti ? valueOptions : valueOptions[0];
  }, [rawValue, isMulti]);

  const handleChange = (newValue: OnChangeValue<Option, IsMulti>) => {
    onChange?.(
      (isMulti
        ? (newValue as MultiValue<Option>).map((opt) => opt.value)
        : (newValue as Option | null)?.value || null) as IdValue<IsMulti>,
    );
  };

  return (
    <Creatable<Option, IsMulti>
      {...styleProps}
      {...fetchOptionsProps}
      {...{ isMulti, ref, getOptionLabel, value }}
      onChange={handleChange}
      isLoading={fetchOptionsProps.isLoading || isLoading}
      placeholder="Select or add a tag…"
      noOptionsMessage={() => "No tags found. Type to add a new tag."}
      {...rest}
    />
  );
}

export default forwardRef(RetailerTagsSelect);
